import * as R from "remeda";
import type { MutableRefObject } from "react";
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { t } from "i18next";
import {
  DropdownPanel,
  IconButton,
  KeyValueCustomSelect,
  stylesSwitchButtonRect,
  SwitchButton,
  Text,
  TextSizes,
  Tooltip,
} from "@smartsuite/react-ui/lib";
import {
  getAvailableFunctions,
  getFunctionConfig,
  getNestedFnConfig,
} from "./utils/function-utils";
import { ArgumentEditor } from "../../../../Forms/InputForm/ArgumentEditor";
import { ChangeFn } from "../ChangeFn";
import { WhereBtn } from "./WhereBtn";
import {
  ArgTitle,
  Argument,
  ConditionTriggerWrapper,
  ErrorRow,
  IconWrapper,
  Root,
  Section,
  Selector,
  Separator,
} from "./style";
import type {
  ConditionFieldDescription,
  ConditionFunctionConfig,
  ConditionsConfig_ConditionConfig,
  IntegrationDescription,
  ValueConfig,
} from "@smartsuite/automation-engine-api";
import { ConditionsLogicOperator } from "@smartsuite/automation-engine-api";
import type { CandidateStore } from "../../../../../../stores/automation/candidate.store";
import { useTheme } from "../../../../../../stores";
import { getLocalCssColorVariables } from "../../../../../../utils/cssVariablesUtils";
import { TriggerLabel } from "../../../../../../utils/constants";
import { ConditionSelectInput } from "./ConditionSelectInput";
import { getDefaultFunction } from "./utils/default-function";
import { getSelectedFunction } from "./utils/selected-function";
import type { ActionCandidate } from "../../../../../../types/candidate";
import { useFieldMissingValidation } from "../../../../../../stores/automation/hooks/field-missing-validation";
import { FieldMissingError } from "../../../ErrorSection";
import { ConditionOptions } from "./ConditionOptions";
import { SubConditionOptions } from "./SubConditionOptions";
import type { BranchRef } from "../../../../../../stores/automation/branches.store";
import { useConditionLeftSources } from "./left-source/use-condition-left-sources";
import { useConditionSourceActions } from "./use-condition-source-actions";
import { resolveReferencedConditionField } from "./left-source/resolve-referenced-condition-field";
import { ConditionLeftSelector } from "./left-source/ConditionLeftSelector";
import { usePillTooltip } from "../../../../../../utils/make-tooltip.hook";

export const ConditionItem: React.FC<{
  condition: ConditionsConfig_ConditionConfig;
  conditionFields: ConditionFieldDescription[];
  candidate: CandidateStore;
  integrations: IntegrationDescription[];
  isInitial: boolean;
  operator: ConditionsLogicOperator;
  onChange: (condition: ConditionsConfig_ConditionConfig) => void;
  onDelete: (condition: ConditionsConfig_ConditionConfig) => void;
  allowDelete: boolean;
  switchOperator: () => void;
  action?: ActionCandidate;
  /** Whether an unset operator surfaces the trigger-submitted error state.
   * Defaults to the historical inference "no action = trigger conditions";
   * the branch panel passes false -- its conditions reuse the trigger's
   * condition fields, but not the trigger's submit-driven validation. */
  showTriggerConditionErrors?: boolean;
  /** Forces the Find-Record condition set (no "Changes"/"Any" operators)
   * regardless of trigger. The Conditional Logic branch panel passes true:
   * branch conditions must never offer the "Changes" operator (spec 3.4). */
  excludeChangesFn?: boolean;
  /** Left-side variant. "static" (default) keeps the trigger-field dropdown for
   * Trigger/Find conditions. "reference" (CL branch conditions) renders the
   * source-aware ConditionLeftSelector and stores an outputReference. */
  leftSide?: "static" | "reference";
  /** Required when leftSide === "reference": the branch whose conditions these
   * are, used to scope available sources to the branch's own execution path. */
  branchRef?: BranchRef;
  /** Reveals the "left side not picked yet" error on this row. Submit-driven:
   * a freshly added row is legitimately empty, so the branch panel passes the
   * candidate's submitted flag rather than true. */
  showIncompleteError?: boolean;
}> = ({
  condition,
  conditionFields,
  candidate,
  integrations,
  onChange,
  onDelete,
  isInitial,
  operator,
  allowDelete,
  switchOperator,
  action,
  showTriggerConditionErrors,
  excludeChangesFn,
  leftSide = "static",
  branchRef,
  showIncompleteError,
}) => {
  const theme = useTheme();
  const [args, setArgs] = useState<
    Array<{ value: ValueConfig | undefined; name: string }>
  >([]);
  const [argValue, setArgValue] = useState<ValueConfig | undefined>();
  const [changeValue, setChangeValue] = useState<
    ConditionFunctionConfig | undefined
  >();
  const [conditionKind, setConditionKind] = useState<ValueConfig | string>();
  const [tab, setTab] = useState<"root" | "sub-menu">("root");
  const [openCondition, setOpenCondition] = useState<boolean>(
    condition.conditionFieldId === "" ||
      conditionFields.find(
        (option) => option.conditionFieldId === condition.conditionFieldId,
      ) === undefined,
  );
  const [openFunction, setOpenFunction] = useState<boolean>(false);
  const [nestedOptions, setNestedOptions] = useState<
    ConditionFieldDescription[]
  >([]);
  const [submenuTitle, setSubmenuTitle] = useState<string>("");
  const { getConditionFieldMissingErrorMessage } = useFieldMissingValidation();

  const fieldDescription = useMemo(
    (): ConditionFieldDescription | undefined =>
      conditionFields.find(
        (d) => d.conditionFieldId === condition.conditionFieldId,
      ),
    [condition, conditionFields],
  );

  const isReference = leftSide === "reference";
  const effectiveBranchRef = branchRef ?? {
    instanceId: -1,
    branchHash: "else",
  };
  const leftSources = useConditionLeftSources(
    candidate,
    effectiveBranchRef,
    integrations,
  );
  // Data sources for the RIGHT side of a branch condition: scoped to the
  // block's execution path, the same list the left side offers. Deliberately
  // NOT derived from the left side's resolved action -- that coupling hid every
  // action whenever the left field came from the trigger (BUG-21770).
  const conditionSourceActions = useConditionSourceActions(
    candidate,
    effectiveBranchRef,
  );
  // Current Item on the RIGHT side comes from the loop enclosing this BLOCK --
  // the same scope the left side's Current Item source uses. Letting the
  // editors derive it from the left side's action resolved to the loop's source
  // action, which sits outside the loop, so Current Item vanished (BUG-21770).
  const conditionLoopScope = candidate.looping.scopeForGroup(
    effectiveBranchRef.instanceId,
  );
  // Content fingerprint for the `resolved` memo below. `candidate` is a stable
  // MobX object identity, so keying the memo on `candidate` alone misses
  // updates when a referenced source's conditionFields change in place
  // without a new candidate/outputReference identity. Computed fresh every
  // render (deliberately not memoized) so its VALUE reflects current content;
  // the memo below still only recomputes when this primitive string changes.
  // Reference mode only cares whose fields these are, but it's computed
  // unconditionally to keep this a plain value, not a conditional hook dep.
  const conditionFieldsFingerprint = [
    fieldsSignature(candidate.self.trigger?.description?.conditionFields),
    ...(candidate.allActions ?? []).map((a) =>
      fieldsSignature(a.description?.conditionFields),
    ),
  ].join("|");
  const resolved = useMemo(
    () =>
      isReference
        ? resolveReferencedConditionField(candidate, condition.outputReference)
        : undefined,
    // eslint-disable-next-line react-hooks/exhaustive-deps -- conditionFieldsFingerprint keys the referenced source's conditionFields content
    [
      isReference,
      candidate,
      condition.outputReference,
      conditionFieldsFingerprint,
    ],
  );
  const referenceFieldDescription = resolved?.fieldDescription;
  const referenceSourceEntity = resolved?.sourceEntity;
  // Narrow the resolved source to an ActionCandidate when it is an action, so
  // operator derivation and the right-side "+" scope to that same action.
  const referenceAction =
    referenceSourceEntity !== undefined &&
    "actionReference" in referenceSourceEntity
      ? referenceSourceEntity
      : undefined;

  // Source context for the left pill's hover tooltip -- built by the same
  // helpers the right-side value pills use, so both sides read identically.
  const { makeTriggerPillTooltip, makeActionPillTooltip } = usePillTooltip();
  const referencePillTooltip =
    referenceSourceEntity === undefined
      ? undefined
      : referenceAction !== undefined
        ? makeActionPillTooltip(referenceAction)
        : makeTriggerPillTooltip(referenceSourceEntity);

  // In reference mode operators derive from the resolved field; otherwise from
  // the static conditionFields lookup (unchanged for Trigger/Find conditions).
  const effectiveFieldDescription = isReference
    ? referenceFieldDescription
    : fieldDescription;
  const functionConfigs = useMemo(
    () =>
      getAvailableFunctions(
        getNestedFnConfig(
          candidate.self.trigger,
          !isReference,
          isReference ? referenceAction : action,
          excludeChangesFn,
        ),
        effectiveFieldDescription,
        condition.function?.function.oneofKind,
      ),
    [
      candidate.self.trigger,
      isReference,
      referenceAction,
      action,
      excludeChangesFn,
      effectiveFieldDescription,
      condition.function,
    ],
  );

  const buttonRef = useRef<HTMLDivElement>(null);

  const onChangeLocal = (
    currentCondition: ConditionsConfig_ConditionConfig,
  ): void => {
    if (JSON.stringify(condition) !== JSON.stringify(currentCondition)) {
      onChange(currentCondition);
    }
  };

  useEffect(() => {
    if (condition.function?.function.oneofKind !== undefined) {
      const argList = getArgList(condition.function);
      setArgs(argList);
      setConditionKind(condition.function?.function.oneofKind);

      const func: ConditionFunctionConfig = condition.function ?? {
        function: { oneofKind: undefined },
      };

      const functionName = func.function.oneofKind;
      const argName = argList[0]?.name;
      // @ts-expect-error
      const newValue = func.function[functionName][argName];
      setArgValue(newValue);

      if (functionName === "changes") {
        setChangeValue(func);
      }
    } else {
      const defaultFunction = getDefaultFunction(
        functionConfigs,
        isReference
          ? referenceFieldDescription
          : conditionFields.find(
              (field) => field.conditionFieldId === condition.conditionFieldId,
            ),
        candidate.self.trigger.triggerReference?.triggerId,
        isReference
          ? referenceAction?.actionReference?.actionId
          : action?.actionReference?.actionId,
        isReference || excludeChangesFn,
      );

      if (defaultFunction !== undefined) {
        setArgs(getArgList(defaultFunction));
        setConditionKind(defaultFunction.function.oneofKind);
        onChange(
          isReference
            ? {
                conditionFieldId: "",
                outputReference: condition.outputReference,
                function: defaultFunction,
              }
            : {
                conditionFieldId: condition.conditionFieldId,
                function: defaultFunction,
              },
        );
      }
    }
  }, [
    condition.function,
    condition.conditionFieldId,
    condition.outputReference,
    conditionFields,
    candidate.self.trigger.triggerReference?.triggerId,
    action?.actionReference?.actionId,
    isReference,
    referenceFieldDescription,
    referenceAction,
    excludeChangesFn,
    functionConfigs,
    onChange,
  ]);

  const handleArgChange = (
    functionName: string,
    argName: string,
    value: unknown,
  ): void => {
    const func: ConditionFunctionConfig = condition.function ?? {
      function: { oneofKind: undefined },
    };

    if (func.function.oneofKind === undefined || functionName === "") {
      return;
    }

    const nextFunction: ConditionFunctionConfig = {
      function: {
        ...func.function,
        [functionName]: {
          // @ts-expect-error indexed access on oneof payload
          ...func.function[functionName],
          [argName]: value,
        },
      },
    };

    onChangeLocal(
      isReference
        ? {
            conditionFieldId: "",
            outputReference: condition.outputReference,
            function: nextFunction,
          }
        : {
            conditionFieldId: condition.conditionFieldId,
            function: nextFunction,
          },
    );
  };

  const goSubMenu = (
    options: ConditionFieldDescription[],
    label: string,
  ): void => {
    setTab("sub-menu");
    setNestedOptions(options);
    setSubmenuTitle(label);
  };

  const currentChanges =
    changeValue?.function.oneofKind === "changes"
      ? changeValue.function.changes
      : condition.function?.function.oneofKind === "changes"
        ? condition.function.function.changes
        : undefined;

  const latestChangesRef = useRef(currentChanges);
  const conditionFieldIdRef = useRef(condition.conditionFieldId);
  useEffect(() => {
    latestChangesRef.current = currentChanges;
    conditionFieldIdRef.current = condition.conditionFieldId;
  });

  const fieldMissingMessage = getConditionFieldMissingErrorMessage(
    condition,
    action,
  );

  // Reference-mode keeps conditionFieldId empty by contract (the engine rejects
  // a branch condition that sets it), so the left side lives in outputReference
  // there and in conditionFieldId otherwise.
  const hasLeftSide = isReference
    ? condition.outputReference !== undefined
    : condition.conditionFieldId !== "";
  const rowErrorMessage = R.isTruthy(fieldMissingMessage)
    ? fieldMissingMessage
    : showIncompleteError === true && !hasLeftSide
      ? String(t("automation-form.this-field-is-required"))
      : "";

  return (
    <>
      <Root $hasError={R.isTruthy(rowErrorMessage)}>
        <ConditionTriggerWrapper>
          {isInitial ? (
            <WhereBtn />
          ) : (
            <SwitchButton
              currentValue={String(operator)}
              options={[
                {
                  value: String(ConditionsLogicOperator.OPERATOR_AND),
                  label: t("general.and").toUpperCase(),
                },
                {
                  value: String(ConditionsLogicOperator.OPERATOR_OR),
                  label: t("general.or").toUpperCase(),
                },
              ]}
              onChange={switchOperator}
              styles={{
                ...stylesSwitchButtonRect,
                height: 36,
                heightSlider: 36,
                widthSlider: 40,
                widthItem: 40,
                gapItem: 5,
                textSize: TextSizes.XS,
              }}
            />
          )}
        </ConditionTriggerWrapper>

        <Section>
          <Selector>
            {isReference ? (
              <ConditionLeftSelector
                sources={leftSources}
                outputReference={condition.outputReference}
                selectedLabel={referenceFieldDescription?.label}
                selectedIcon={referenceFieldDescription?.icon}
                selectedTooltip={referencePillTooltip}
                onChange={(outputReference) => {
                  // Decision 2: changing the left field resets operator + right
                  // side. Emitting the reference with no function clears them;
                  // the default-function effect then repopulates the correct
                  // default for the new field type.
                  // conditionFieldId is forced empty (not carried over): the
                  // engine's save validation requires reference-mode (branch)
                  // conditions to leave condition_field_id unset, and legacy
                  // branch conditions may still hold a non-empty value.
                  onChangeLocal({
                    conditionFieldId: "",
                    outputReference,
                  });
                }}
              />
            ) : (
              <>
                <DropdownPanel
                  my="left top+2"
                  at="left bottom+2"
                  minWidth={200}
                  portalStyles={getLocalCssColorVariables(theme)}
                  isActive={openCondition}
                  targetElementRef={buttonRef as MutableRefObject<HTMLElement>}
                  onClickOutside={() => {
                    setOpenCondition(false);
                  }}
                >
                  {tab === "root" && (
                    <ConditionOptions
                      condition={condition}
                      onChange={(e) => {
                        onChangeLocal(e);
                      }}
                      conditionFields={conditionFields}
                      action={action}
                      goSubMenu={goSubMenu}
                    />
                  )}
                  {tab === "sub-menu" && (
                    <SubConditionOptions
                      condition={condition}
                      onChange={(e) => {
                        onChangeLocal(e);
                      }}
                      conditionFields={nestedOptions}
                      title={submenuTitle}
                      onNavigateBack={() => setTab("root")}
                    />
                  )}
                </DropdownPanel>
                <ConditionSelectInput
                  openCondition={openCondition}
                  conditionFields={conditionFields}
                  condition={condition}
                  fieldDescription={fieldDescription}
                  handleClick={() => setOpenCondition(!openCondition)}
                  buttonRef={buttonRef}
                />
              </>
            )}
          </Selector>
          {R.isTruthy(effectiveFieldDescription) && (
            <Selector>
              <KeyValueCustomSelect
                state={getSelectorState(
                  openFunction,
                  (showTriggerConditionErrors ?? action === undefined)
                    ? candidate
                    : undefined,
                  condition.function?.function.oneofKind,
                )}
                onOpenedChange={setOpenFunction}
                showClear={false}
                value={conditionKind}
                placeholder={
                  openFunction ? t("automation-form.operator") : "\u00a0"
                }
                placeholderArrowStyle="filled-dark"
                searchPlaceholder={t("general.search")}
                dropdownMinWidth={240}
                portalStyles={getLocalCssColorVariables(theme)}
                options={functionConfigs.map(([key, value]) => ({
                  label: value,
                  value: key,
                }))}
                onChange={(value: unknown): void => {
                  setConditionKind(value as ValueConfig);
                  onChangeLocal(
                    isReference
                      ? {
                          conditionFieldId: "",
                          outputReference: condition.outputReference,
                          function: getSelectedFunction(String(value)),
                        }
                      : {
                          conditionFieldId: condition.conditionFieldId,
                          function: getSelectedFunction(String(value)),
                        },
                  );
                }}
              />
            </Selector>
          )}
          {args.length === 1 && (
            <Argument>
              {args.map((arg) => (
                <Fragment key={arg.name}>
                  {R.isTruthy(effectiveFieldDescription) && (
                    <ArgumentEditor
                      value={argValue === undefined ? { values: [] } : argValue}
                      fieldDescription={effectiveFieldDescription}
                      functionConfig={getFunctionConfig(
                        functionConfigs,
                        condition.function?.function.oneofKind,
                      )}
                      candidate={candidate}
                      action={isReference ? referenceAction : action}
                      sourceActions={
                        isReference ? conditionSourceActions : undefined
                      }
                      loopScope={isReference ? conditionLoopScope : undefined}
                      integrations={integrations}
                      onChange={(value) => {
                        setArgValue(value);
                        handleArgChange(
                          condition.function?.function.oneofKind ?? "",
                          arg.name,
                          value,
                        );
                      }}
                    />
                  )}
                </Fragment>
              ))}
            </Argument>
          )}
        </Section>
        <IconWrapper>
          <Tooltip
            display="inline-flex"
            placements={["right", "left"]}
            padding={0}
            text="Remove Condition"
            target={
              <IconButton
                size="S"
                name="trash-duotone"
                disabled={!allowDelete}
                ariaLabel="Remove Condition"
                onClick={() => (allowDelete ? onDelete(condition) : false)}
              />
            }
            disabled={!allowDelete}
            className="trash-bin-icon"
          />
        </IconWrapper>
      </Root>

      {R.isTruthy(rowErrorMessage) && (
        <ErrorRow>
          <FieldMissingError message={rowErrorMessage} />
        </ErrorRow>
      )}

      {isFunctionSelected(conditionKind) && conditionKind === "changes" && (
        <>
          <Root smallMargin>
            <ConditionTriggerWrapper>
              <ArgTitle>
                <Text
                  size="xs"
                  weight="semibold"
                  color="secondary"
                  transformMode="uppercase"
                >
                  {t("general.from")}
                </Text>
              </ArgTitle>
            </ConditionTriggerWrapper>
            <Separator />
            <ChangeFn
              key="changes-from"
              inputIdPrefix="changes-from"
              conditionFn={
                changeValue?.function.oneofKind === "changes"
                  ? changeValue?.function.changes.before
                  : undefined
              }
              availableFns={getAvailableFunctions(
                getNestedFnConfig(
                  candidate.self.trigger,
                  false,
                  action,
                  excludeChangesFn,
                ),
                fieldDescription,
                changeValue?.function.oneofKind === "changes"
                  ? changeValue.function.changes.before?.function.oneofKind
                  : undefined,
              )}
              fieldDescription={fieldDescription}
              candidate={candidate}
              integrations={integrations}
              onChange={(conditionFn) => {
                if (conditionKind === "changes") {
                  const latestChanges = latestChangesRef.current;
                  setChangeValue({
                    function: {
                      oneofKind: "changes",
                      changes: {
                        ...latestChanges,
                        before: conditionFn,
                      },
                    },
                  });
                  onChangeLocal({
                    conditionFieldId: conditionFieldIdRef.current,
                    function: {
                      function: {
                        oneofKind: "changes",
                        changes: {
                          ...latestChanges,
                          before: conditionFn,
                        },
                      },
                    },
                  });
                }
              }}
            />
          </Root>
          <Root smallMargin>
            <ConditionTriggerWrapper>
              <ArgTitle>
                <Text
                  size="xs"
                  weight="semibold"
                  color="secondary"
                  transformMode="uppercase"
                >
                  {t("general.to")}
                </Text>
              </ArgTitle>
            </ConditionTriggerWrapper>
            <Separator />
            <ChangeFn
              key="changes-to"
              inputIdPrefix="changes-to"
              conditionFn={
                changeValue?.function.oneofKind === "changes"
                  ? changeValue?.function.changes.after
                  : undefined
              }
              availableFns={getAvailableFunctions(
                getNestedFnConfig(
                  candidate.self.trigger,
                  false,
                  undefined,
                  excludeChangesFn,
                ),
                fieldDescription,
                changeValue?.function.oneofKind === "changes"
                  ? changeValue.function.changes.after?.function.oneofKind
                  : undefined,
              )}
              fieldDescription={fieldDescription}
              candidate={candidate}
              integrations={integrations}
              onChange={(conditionFn) => {
                if (conditionKind === "changes") {
                  const latestChanges = latestChangesRef.current;
                  setChangeValue({
                    function: {
                      oneofKind: "changes",
                      changes: {
                        ...latestChanges,
                        after: conditionFn,
                      },
                    },
                  });
                  onChangeLocal({
                    conditionFieldId: conditionFieldIdRef.current,
                    function: {
                      function: {
                        oneofKind: "changes",
                        changes: {
                          ...latestChanges,
                          after: conditionFn,
                        },
                      },
                    },
                  });
                }
              }}
            />
          </Root>
        </>
      )}
    </>
  );
};

// Joins each field's id + editor kind into a stable per-source signature, so
// the conditionFieldsFingerprint above changes whenever a source's condition
// fields are added, removed, or change editor type.
const fieldsSignature = (
  fields: ConditionFieldDescription[] | undefined,
): string =>
  (fields ?? [])
    .map((f) => `${f.conditionFieldId}:${f.editor?.editor.oneofKind ?? ""}`)
    .join(",");

const isFunctionSelected = (
  condition: ValueConfig | string | undefined,
): boolean => condition !== undefined;

const getArgList = (
  conditionFn: ConditionFunctionConfig,
): Array<{ value: ValueConfig | undefined; name: string }> => {
  switch (conditionFn.function.oneofKind) {
    case "isEquals":
      return [{ value: conditionFn.function.isEquals.right, name: "right" }];
    case "isNotEquals":
      return [{ value: conditionFn.function.isNotEquals.right, name: "right" }];
    case "isAnyOf":
      return [{ value: conditionFn.function.isAnyOf.right, name: "right" }];
    case "isNoneOf":
      return [{ value: conditionFn.function.isNoneOf.right, name: "right" }];
    case "isEmpty":
      return [];
    case "isNotEmpty":
      return [];
    case "isAny":
      return [];
    case "hasAnyOf":
      return [
        { value: conditionFn.function.hasAnyOf.haystack, name: "haystack" },
      ];
    case "hasAllOf":
      return [
        { value: conditionFn.function.hasAllOf.haystack, name: "haystack" },
      ];
    case "hasNoneOf":
      return [
        { value: conditionFn.function.hasNoneOf.haystack, name: "haystack" },
      ];
    case "greaterThan":
      return [{ value: conditionFn.function.greaterThan.right, name: "right" }];
    case "greaterThanOrEquals":
      return [
        {
          value: conditionFn.function.greaterThanOrEquals.right,
          name: "right",
        },
      ];
    case "lessThan":
      return [{ value: conditionFn.function.lessThan.right, name: "right" }];
    case "lessThanOrEquals":
      return [
        { value: conditionFn.function.lessThanOrEquals.right, name: "right" },
      ];
    case "between":
      return [
        { value: conditionFn.function.between.min, name: "min" },
        { value: conditionFn.function.between.max, name: "max" },
      ];
    case "betweenOrEquals":
      return [
        { value: conditionFn.function.betweenOrEquals.min, name: "min" },
        { value: conditionFn.function.betweenOrEquals.max, name: "max" },
      ];
    case "contains":
      return [{ value: conditionFn.function.contains.right, name: "right" }];
    case "notContains":
      return [{ value: conditionFn.function.notContains.right, name: "right" }];
    case "changes":
      return []; // to fix later
    case "allItemsResolved":
      return [];
    case "notAllItemsResolved":
      return [];
    case "percentCompleteGreaterThan":
      return [
        {
          value: conditionFn.function.percentCompleteGreaterThan.right,
          name: "right",
        },
      ];
    case "percentCompleteLessThan":
      return [
        {
          value: conditionFn.function.percentCompleteLessThan.right,
          name: "right",
        },
      ];
    case "isComplete":
      return [];
    case "isNotComplete":
      return [];
    default:
      return [];
  }
};

const getSelectorState = (
  isOpen: boolean,
  candidate?: CandidateStore,
  value?: string,
): "active" | "default" | "error" => {
  if (
    candidate?.isTriggerSubmitted &&
    candidate.self.trigger.triggerReference?.triggerId ===
      TriggerLabel.matchesCondition &&
    R.isNil(value)
  ) {
    return "error";
  } else if (isOpen) {
    return "active";
  } else {
    return "default";
  }
};
